import { autoUpdate, FloatingPortal, useFloating } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import React from 'react'
import { coverReferenceMiddleware } from '../../utilities'
import { HighlightOverlay } from '../structural'

export interface LiveFieldProps {
	value: string
	onChange: (value: string) => void
	placeholder?: string
	isGenerating?: boolean
	fitSize?: boolean
	disabled?: boolean
	className?: string
	style?: React.CSSProperties
	showHoverRing?: boolean
}

export function LiveField(
	{
		value,
		onChange,
		placeholder = '...',
		isGenerating = false,
		fitSize = true,
		disabled = false,
		className: externalClassName,
		style: externalStyle,
		showHoverRing = true,
	}: LiveFieldProps,
) {
	const [contentEditableElement, setContentEditableElement] = React.useState<HTMLDivElement | null>(null)
	const isComposingRef = React.useRef(false)
	const [isHovered, setIsHovered] = React.useState(false)

	const { refs: hoverRingRefs, floatingStyles: hoverRingStyles } = useFloating({
		open: showHoverRing && isHovered,
		middleware: coverReferenceMiddleware(),
		whileElementsMounted: autoUpdate,
	})

	// Memoize ref callback to prevent infinite loop during re-renders
	const refCallback = React.useCallback((node: HTMLDivElement | null) => {
		setContentEditableElement(node)
		hoverRingRefs.setReference(node)
	}, [])

	// Sync DOM content with `value` prop
	React.useEffect(() => {
		if (contentEditableElement && !isComposingRef.current) {
			const current = normalizeHtml(contentEditableElement.innerHTML)
			if (current !== value) {
				// Save cursor position
				const selection = window.getSelection()
				const savedRange = selection && selection.rangeCount > 0
					? {
						startContainer: selection.getRangeAt(0).startContainer,
						startOffset: selection.getRangeAt(0).startOffset,
						endContainer: selection.getRangeAt(0).endContainer,
						endOffset: selection.getRangeAt(0).endOffset,
					}
					: null

				// Update content
				contentEditableElement.innerHTML = escapeHtml(value).replace(/\n/g, '<br>')

				// Restore cursor position if the element still has focus
				if (savedRange && document.activeElement === contentEditableElement) {
					try {
						const range = document.createRange()
						// Check if the saved containers are still in the DOM
						if (contentEditableElement.contains(savedRange.startContainer)) {
							range.setStart(
								savedRange.startContainer,
								Math.min(savedRange.startOffset, savedRange.startContainer.textContent?.length || 0),
							)
							if (contentEditableElement.contains(savedRange.endContainer)) {
								range.setEnd(
									savedRange.endContainer,
									Math.min(savedRange.endOffset, savedRange.endContainer.textContent?.length || 0),
								)
							} else {
								range.collapse(true)
							}
							selection?.removeAllRanges()
							selection?.addRange(range)
						}
					} catch (e) {
						// If restoration fails, just continue - cursor will be at the end
					}
				}
			}
		}
	}, [value, contentEditableElement])

	const handleInput = () => {
		if (!contentEditableElement || isComposingRef.current) { return }
		const normalized = normalizeHtml(contentEditableElement.innerHTML)
		onChange(normalized)
	}

	const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
		e.preventDefault()
		const text = e.clipboardData.getData('text/plain')
		document.execCommand('insertText', false, text)
	}

	const handleCompositionStart = () => {
		isComposingRef.current = true
	}

	const handleCompositionEnd = () => {
		isComposingRef.current = false
		// Emit the final composed value now that the IME session is done
		if (!contentEditableElement) { return }
		const normalized = normalizeHtml(contentEditableElement.innerHTML)
		onChange(normalized)
	}

	if (isGenerating) {
		return <div className="h-[1em] w-full min-w-10 bg-grey-200 rounded-full animate-pulse" />
	}

	const defaultClassName = ctl(`
		block relative
		${fitSize ? '' : 'min-w-full max-w-full'}
		[white-space:pre-wrap] [word-break:break-word] [overflow-wrap:break-word] min-h-[1em] min-w-[3ch]
		notranslate
	`)
	const mergedClassName = externalClassName ? `${defaultClassName} ${externalClassName}` : defaultClassName

	return (
		<>
			<div
				ref={refCallback}
				contentEditable={disabled ? undefined : 'plaintext-only'}
				translate="no"
				role="textbox"
				aria-multiline="true"
				aria-placeholder={placeholder}
				spellCheck={false}
				onInput={handleInput}
				onPaste={handlePaste}
				onDrop={(e) => e.preventDefault()}
				onCompositionStart={handleCompositionStart}
				onCompositionEnd={handleCompositionEnd}
				onMouseEnter={showHoverRing ? () => setIsHovered(true) : undefined}
				onMouseLeave={showHoverRing ? () => setIsHovered(false) : undefined}
				className={mergedClassName}
				style={{
					outline: 'none',
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					overflowWrap: 'break-word',
					minHeight: '1em',
					...externalStyle,
				}}
			/>
			{showHoverRing && isHovered && (
				<FloatingPortal>
					<div
						ref={hoverRingRefs.setFloating}
						style={{ ...hoverRingStyles, pointerEvents: 'none' }}
						data-render-exclude
					>
						<HighlightOverlay />
					</div>
				</FloatingPortal>
			)}
		</>
	)
}

/**
 * Converts innerHTML from the contenteditable into plain text
 * with explicit `\n` for block breaks.
 */
function normalizeHtml(html: string): string {
	// Replace divs and <br> with newlines
	// IMPORTANT: Decode &amp; FIRST to prevent double-decoding XSS vulnerability
	return html
		.replace(/<\/div><div>/gi, '\n')
		.replace(/<div>/gi, '')
		.replace(/<\/div>/gi, '')
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/&amp;/gi, '&') // Must decode & first!
		.replace(/&nbsp;/gi, ' ')
		.replace(/&lt;/gi, '<')
		.replace(/&gt;/gi, '>')
}

/** Escapes text so we can safely set innerHTML */
function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
}
